// Define types for the input and output
interface Build {
  pageUrl: string;
  downloadUrl: string;
  version: string;
  build: string;
  releaseNotes: string;
  size: number;
  uploadedAt: string;
}

interface App {
  domain: string;
  appId: string;
  appName: string;
  appDesc: string;
  platform: string;
  builds: Record<string, Build[]>; // e.g., { BETA: Build[], STABLE: Build[] }
}

export interface TransformedData {
  domain: string;
  appId: string;
  appName: string;
  appDesc: string;
  platform: string;
  versions: string[];
  data: Record<string, Build[]>; // version as key, array of builds as value
}

const transformBuildData = (response: App[], buildType: string): TransformedData[]  => {
  // Get unique domains from the data
  if (!Array.isArray(response) || !['STABLE', 'BETA'].includes(buildType)) {
    return [];
  }

  // Transform data for each domain
  return response.map(app => {
    // Filter builds based on the specified buildType
    const filteredBuilds = app.builds[buildType] || [];

    // Extract unique versions and sort them in descending order
    const versions = Array.from(new Set(filteredBuilds.map(build => build.version)))
      .sort((a, b) => {
        const [majorA, minorA, patchA] = a.split('.').map(Number);
        const [majorB, minorB, patchB] = b.split('.').map(Number);
        return majorB - majorA || minorB - minorA || patchB - patchA;
      });

    // Create data object with version as key and build details as value
    const data = filteredBuilds.reduce<Record<string, Build[]>>((acc, build) => {
      if (!acc[build.version]) {
        acc[build.version] = [];
      }
      acc[build.version].push({
        pageUrl: build.pageUrl,
        downloadUrl: build.downloadUrl,
        version: build.version,
        build: build.build,
        releaseNotes: build.releaseNotes,
        size: build.size,
        uploadedAt: build.uploadedAt
      });
      return acc;
    }, {});

    return {
      domain: app.domain,
      appId: app.appId,
      appName: app.appName,
      appDesc: app.appDesc,
      platform: app.platform,
      versions,
      data
    };
});
}

export default transformBuildData;